import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../firebaseConfig';

/**
 * previousEmailContext — loads the email that was last sent to a contact so the
 * follow-up writer can read it for style/content reference.
 *
 * Source of truth is the `world_sent/{userId}/emails` collection (same store the
 * EmailHistoryPanel reads). A zero-read fast path uses `contact.lead_last_email`
 * when the backend already persisted its body there.
 */

const MAX_BODY_CHARS = 2500;

const toMillis = (value) => {
  if (value == null) return 0;
  if (typeof value === 'object' && typeof value.toMillis === 'function') return value.toMillis();
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

// Convert a sent-mail body (HTML or plain) into readable plain text and cap its
// length, so the writer gets the message — not markup or an endless thread.
const normalizeBody = (raw) => {
  if (!raw) return '';
  const text = String(raw)
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<\/(p|div|h[1-6]|li|tr|blockquote)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!text) return '';
  return text.length > MAX_BODY_CHARS ? `${text.slice(0, MAX_BODY_CHARS).trim()}…` : text;
};

// Topic types that represent a follow-up — i.e. there is a prior email to reference.
export const isFollowUpTopicType = (topicType) =>
  topicType === 'follow_up_1' || topicType === 'follow_up_2' || topicType === 'follow_up_3';

/**
 * Returns the previously sent email as `{ subject, body }` (plain text), or
 * `null` when none can be found. Never throws.
 */
export const fetchPreviousSentEmail = async (userId, contact, recipientEmails = []) => {
  if (!userId || !contact) return null;

  // 1) Fast path (no read): backend-written lead_last_email already carries body.
  const lle = contact.lead_last_email;
  if (lle && (lle.content || lle.body)) {
    const body = normalizeBody(lle.content || lle.body);
    if (body) return { subject: lle.subject || '', body };
  }

  // 2) Fallback: pull the newest mail we actually sent this contact from world_sent.
  const contactId = contact.id || null;
  const addresses = (recipientEmails || [])
    .map((e) => (e || '').trim().toLowerCase())
    .filter(Boolean);

  try {
    const sentRef = collection(firestore, `world_sent/${userId}/emails`);
    const seen = new Set();
    const collected = [];
    const pushDoc = (d) => {
      if (seen.has(d.id)) return;
      seen.add(d.id);
      const data = d.data();
      collected.push({ ...data, _ms: toMillis(data.timestamp || data.sentAt || data.date || data.createdAt) });
    };

    if (contactId) {
      try {
        const snap = await getDocs(query(sentRef, where('leadId', '==', contactId)));
        snap.docs.forEach(pushDoc);
      } catch (_) { /* ignore — fall through to address match */ }
    }

    if (collected.length === 0) {
      for (const addr of addresses) {
        try {
          const snap = await getDocs(query(sentRef, where('to', '==', addr)));
          snap.docs.forEach(pushDoc);
        } catch (_) { /* ignore */ }
      }
    }

    if (collected.length === 0) return null;

    collected.sort((a, b) => b._ms - a._ms);
    const newest = collected[0];
    const body = normalizeBody(newest.bodyHtml || newest.content || newest.bodyText || newest.text || '');
    if (!body) return null;
    return { subject: newest.subject || '', body };
  } catch (err) {
    console.warn('[previousEmailContext] fetch failed:', err?.message);
    return null;
  }
};
